import {useState, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {useMediaQueries} from 'presentation/pages/private/hooks';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useGetObligationPdfQuery} from 'presentation/pages/private/my-payments-flow/tab-pages/billet-details/queries/useGetObligationPdfQuery';

import {OpenBillStatusEnum} from '../../../OpenBillsTable/types';
import type {UseDetailsDrawerProps, UseDetailsDrawerReturn} from './types';

export const useDetailsDrawer = ({
	open,
	row,
	hasAutoPayment,
}: UseDetailsDrawerProps): UseDetailsDrawerReturn => {
	const {isMobile} = useMediaQueries();
	const queryClient = useQueryClient();
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLElement | null>(null);
	const [modalErrorState, setModalErrorState] = useState(false);
	const [selectedObligation, setSelectedObligation] = useState<number | null>(null);
	const [shouldOpenPdf, setShouldOpenPdf] = useState(false);

	useEffect(() => {
		if (!open) {
			setTooltipOpen(false);
			setTooltipAnchorEl(null);
			setSelectedObligation(null);
			setShouldOpenPdf(false);
		}
	}, [open]);

	const isOpenBillWithAutoPayment = !!(hasAutoPayment && row?.status === OpenBillStatusEnum.OPEN);

	const {
		data: pdfData,
		isLoading: isLoadingPdf,
		isFetching: isFetchingPdf,
		isError: isErrorPdf,
	} = useGetObligationPdfQuery({
		obrigacao: selectedObligation ?? 0,
		responseType: 'blob',
	});

	useEffect(() => {
		if (pdfData && shouldOpenPdf) {
			window.open(URL.createObjectURL(pdfData as Blob));
			setShouldOpenPdf(false);
		}
		if (isErrorPdf) {
			setModalErrorState(true);
			setShouldOpenPdf(false);
			setSelectedObligation(null);
		}
	}, [pdfData, shouldOpenPdf, isErrorPdf]);

	const handleDownload = () => {
		const obrigacao = Number(row?.cdObrigacao);
		if (!obrigacao || shouldOpenPdf || isLoadingPdf || isFetchingPdf) return;
		setSelectedObligation(obrigacao);
		setShouldOpenPdf(true);
	};

	const handleCloseErrorModal = () => {
		setModalErrorState(false);
		queryClient.invalidateQueries({queryKey: [QUERY_KEYS.OBLIGATION_PDF]});
	};

	const handleInfoIconClick = (el: HTMLElement) => {
		setTooltipAnchorEl(el);
		setTooltipOpen(prev => !prev);
	};

	const handleTooltipClose = () => {
		setTooltipOpen(false);
	};

	return {
		isMobile,
		tooltipOpen,
		tooltipAnchorEl,
		isOpenBillWithAutoPayment,
		handleInfoIconClick,
		handleTooltipClose,
		isDownloading: isLoadingPdf || isFetchingPdf || shouldOpenPdf,
		modalErrorState,
		handleDownload,
		handleCloseErrorModal,
	};
};
